import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Input } from "reactstrap";
import { useDispatch } from "react-redux";
import { getAllPrograms } from "../../store/slices/programsSlice";
import { toast } from "react-toastify";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/**
 * ProgramSlugModal
 * Lists all programs with their slug_name and a one-click copy button.
 * Props:  isOpen (bool), toggle (() => void)
 */
const ProgramSlugModal = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const [programs, setPrograms] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setCopiedSlug(null);
    setLoading(true);
    // getBySlugName: true tells the backend to return each program's slug_name
    // (the plain getAllPrograms() call omits slugs, so the copy button had nothing).
    dispatch(getAllPrograms({ getBySlugName: true }))
      .unwrap()
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.programsData)
          ? data.programsData
          : Array.isArray(data?.data)
          ? data.data
          : [];
        setPrograms(list);
      })
      .catch(() => toast.error("Failed to load programs"))
      .finally(() => setLoading(false));
  }, [isOpen, dispatch]);

  const handleCopy = (slug) => {
    navigator.clipboard
      .writeText(slug)
      .then(() => {
        setCopiedSlug(slug);
        toast.success("Copied: " + slug);
        setTimeout(() => setCopiedSlug(null), 2000);
      })
      .catch(() => toast.error("Copy failed"));
  };

  const filtered = programs.filter((p) => {
    const name = (p.program_name || p.name || "").toLowerCase();
    const slug = (p.slug_name || p.slug || "").toLowerCase();
    return name.includes(search.toLowerCase()) || slug.includes(search.toLowerCase());
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>
        <span style={{ fontWeight: 700 }}>Programs & Slug Names</span>
      </ModalHeader>
      <ModalBody style={{ padding: "1rem" }}>
        <Input
          type="text"
          placeholder="Search by program name or slug..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-3"
        />
        {loading ? (
          <div className="text-center py-4 text-muted">Loading programs...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-4 text-muted">No programs found.</div>
        ) : (
          <table className="table table-bordered table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "50%" }}>Program Name</th>
                <th style={{ width: "40%" }}>Slug Name</th>
                <th style={{ width: "10%", textAlign: "center" }}>Copy</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const slug = p.slug_name || p.slug || "";
                const name = p.program_name || p.name || "-";
                const isCopied = copiedSlug === slug;
                return (
                  <tr key={p.id || p.ID || i}>
                    <td>{name}</td>
                    <td>
                      <code style={{ fontSize: "0.85rem", color: "#6f42c1" }}>
                        {slug || <span className="text-muted">-</span>}
                      </code>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {slug ? (
                        <button
                          type="button"
                          title="Copy slug"
                          onClick={() => handleCopy(slug)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "2px 4px",
                            color: isCopied ? "#28a745" : "#6c757d",
                            transition: "color 0.2s",
                          }}
                        >
                          {isCopied
                            ? <CheckIcon fontSize="small" />
                            : <ContentCopyIcon fontSize="small" />}
                        </button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ProgramSlugModal;
